import { SandboxStore } from '../../store';
import { sandboxAdvertisementSeeds } from '../../store/seed/advertisements';
import { SandboxOperationHandler, SandboxVariables } from '../types';

const categories = [
  {
    __typename: 'AdvertisementCategory',
    id: 1,
    title: 'Товары',
    slug: 'GOODS',
  },
  {
    __typename: 'AdvertisementCategory',
    id: 2,
    title: 'Услуги',
    slug: 'SERVICES',
  },
];

const companies = [
  { name: 'Pixel Point Бауманская', city: 'Москва' },
  { name: 'Nova Arena Сокол', city: 'Москва' },
  { name: 'Checkpoint Марьино', city: 'Москва' },
  { name: 'Respawn Club Таганская', city: 'Москва' },
  { name: 'Level Base Химки', city: 'Химки' },
  { name: 'Cyber Loft Мытищи', city: 'Мытищи' },
];

const contacts = [
  'Марина, +7 (000) 000-00-01',
  'Алексей, +7 (000) 000-00-02',
  'Илья, +7 (000) 000-00-03',
  'Софья, +7 (000) 000-00-04',
  'Денис, +7 (000) 000-00-05',
  'Виктория, +7 (000) 000-00-06',
];

const serviceIndexes = new Set([
  6, 9, 11, 13, 14, 15, 16, 17, 18, 19, 20, 21, 24, 29,
]);

const prices = [
  300000, 52000, 4800, 18500, 76000, 31000, 45000, 28000, 4900000, 35000, 18900,
  200, 9800, 25000, 15000, 1000, 7500, 2000, 1200, 1500, 85000, 150, 52000,
  7900, 2000, 38000, 22000, 24500, 21000, 1000,
];

interface SandboxAdvertisement {
  __typename: 'PublicAdvertisement';
  id: string;
  title: string;
  description: string;
  images: string[];
  price: number;
  published_at: string;
  category: typeof categories[number];
  company: {
    __typename: 'Company';
    id: string;
    name: string;
    city: string;
    country: string;
  };
  currency: {
    __typename: 'Currency';
    id: number;
    alias: string;
    letter: string;
  };
  metrics: {
    __typename: 'PublicAdvertisementMetrics';
    views: number;
  };
}

interface SandboxAdvertisementPage<T> {
  __typename: 'AdvertisementPaginator';
  data: T[];
  paginatorInfo: {
    __typename: 'PaginatorInfo';
    currentPage: number;
    lastPage: number;
    hasMorePages: boolean;
    perPage: number;
    total: number;
  };
}

const buildAdvertisements = (store: SandboxStore): SandboxAdvertisement[] =>
  sandboxAdvertisementSeeds.map((seed, index) => {
    const company = companies[index % companies.length];
    const category = categories[serviceIndexes.has(index) ? 1 : 0];

    return {
      __typename: 'PublicAdvertisement',
      id: `demo-ad-${String(index + 1).padStart(3, '0')}`,
      title: seed.title,
      description: seed.description,
      images: [...seed.images],
      price: prices[index],
      published_at: new Date(
        store.getAnchorMs() - (index * 7 + 2) * 3600000
      ).toISOString(),
      category,
      company: {
        __typename: 'Company',
        id: `demo-company-${(index % companies.length) + 1}`,
        name: company.name,
        city: company.city,
        country: 'Россия',
      },
      currency: {
        __typename: 'Currency',
        id: 1,
        alias: 'RUB',
        letter: '₽',
      },
      metrics: {
        __typename: 'PublicAdvertisementMetrics',
        views: 28 + ((index * 47) % 640),
      },
    };
  });

const paginator = <T>(
  items: T[],
  variables: SandboxVariables
): SandboxAdvertisementPage<T> => {
  const first = Math.max(1, Number(variables.first ?? 60));
  const currentPage = Math.max(1, Number(variables.page ?? 1));
  const start = (currentPage - 1) * first;
  const lastPage = Math.max(1, Math.ceil(items.length / first));

  return {
    __typename: 'AdvertisementPaginator',
    data: items.slice(start, start + first),
    paginatorInfo: {
      __typename: 'PaginatorInfo',
      currentPage,
      lastPage,
      hasMorePages: currentPage < lastPage,
      perPage: first,
      total: items.length,
    },
  };
};

const getPublicAdvertisements: SandboxOperationHandler = (variables, store) => {
  const filter =
    (variables.filter as
      | { category_id?: number; query?: string }
      | undefined) ?? {};
  const query = String(filter.query ?? '')
    .trim()
    .toLocaleLowerCase('ru');
  const categoryId = Number(filter.category_id ?? 0);
  const items = buildAdvertisements(store).filter(
    (item) =>
      (!categoryId || item.category.id === categoryId) &&
      (!query ||
        `${item.title} ${item.description}`
          .toLocaleLowerCase('ru')
          .includes(query))
  );

  return {
    getAdvertisements: {
      __typename: 'AdvertisementFeed',
      advertisements: paginator(items, variables),
      partners: [],
    },
  };
};

export const advertisementResolvers: Record<string, SandboxOperationHandler> = {
  GetAdvertisementCategoriesV2: () => ({
    getAdvertisementCategories: categories,
  }),
  GetAdvertisementsV2: getPublicAdvertisements,
  GetPartnerAdvertisementsV2: () => ({
    getAdvertisements: {
      __typename: 'AdvertisementFeed',
      partners: [],
    },
  }),
  GetMyAdvertisementsV2: (variables) => ({
    getMyAdvertisements: paginator([], variables),
  }),
  GetAdvertisementV2: (variables, store) => ({
    getAdvertisement:
      buildAdvertisements(store).find(
        (item) => item.id === String(variables.id)
      ) ?? null,
  }),
  GetAdvertisementContactV2: (variables) => {
    const match = /^demo-ad-(\d+)$/.exec(String(variables.id));
    const index = match ? Number(match[1]) - 1 : -1;
    return {
      getAdvertisementContact:
        index >= 0 ? contacts[index % contacts.length] : '',
    };
  },
};
